import { FeatureFlagCheck } from '../../portal/portal-navigation.constants';

export interface AifcNavItem {
  /** i18n key — the English label doubles as the translation key. */
  labelKey: string;
  /** Path inside the AIFC portal (environment.aifcPortalUrl). */
  externalPath?: string;
  /** Route inside front-ext. */
  routerLink?: string;
  /**
   * Only rendered in the mobile drawer, and only at the top level — mirrors
   * aifc-portal's `sidenav-item.component.html` (`@if (level === 0 && item.icon)`),
   * where nested/dropdown children never get an icon slot regardless of level.
   */
  icon?: string;
  /**
   * Feature flag code that must be on for this entry to render — same contract
   * as `PortalNavItem.featureFlag`. Flag-less entries are always visible; a
   * flagged entry (top level or dropdown child) is dropped by `buildAifcNav`
   * when its flag is off. Lets an internal front-ext section coexist with its
   * external aifc-portal counterpart until the flag flips.
   */
  featureFlag?: string;
  /** Rendered as a dropdown when present. */
  children?: AifcNavItem[];
}

/**
 * AIFC portal landing/entry path. The "Home" nav item points here; the header
 * resolves it to `/dashboard` (authenticated) or `/home` (anonymous) at click
 * time — see `AifcHeaderComponent.resolveExternalUrl`.
 */
export const AIFC_ENTRY_PATH = '/home';

/**
 * Mirrors aifc-portal/src/app/core/navigation/navigation-items.ts 1:1 — only
 * the entries actually active there (it also carries "Data Protection Services",
 * "Procurement" and a "Deal Room" marketplace child, all commented out on that
 * side and left out here to match). "Business Services" is front-ext itself.
 */
export const AIFC_NAV: AifcNavItem[] = [
  { labelKey: 'Home', externalPath: AIFC_ENTRY_PATH, icon: 'appstore-add' },
  { labelKey: 'Business Services', routerLink: '/', icon: 'briefcase' },
  // DR-827: expat migration cut over to front-ext. Single Expat Centre entry routing
  // to the internal module; gated by EXT_EXPAT_CENTRE so it only shows once the
  // migration is live. The legacy external aifc-portal entry has been removed.
  { labelKey: 'Expat Centre', routerLink: '/expat-centre', icon: 'global', featureFlag: 'EXT_EXPAT_CENTRE' },
  // DR-827: property migrated to front-ext. Single Property Rental entry routing to the
  // internal module, gated by EXT_PROPERTY_RENTAL. Legacy external aifc-portal entry removed.
  { labelKey: 'Property Rental', routerLink: '/property', icon: 'home', featureFlag: 'EXT_PROPERTY_RENTAL' },
  {
    labelKey: 'Marketplaces',
    icon: 'shopping-cart',
    children: [
      // DR-1068: ASP Marketplace now served by front-ext itself (route '/marketplace').
      { labelKey: 'ASP Marketplace', externalPath: '/marketplace' },
      { labelKey: 'ASP Marketplace Test', routerLink: '/marketplace', featureFlag: 'EXT_MARKETPLACE' },
      { labelKey: 'Financial Products', externalPath: '/financial-products' },
      { labelKey: 'Financial Products Test', routerLink: '/financial-products', featureFlag: 'EXT_FINANCIAL_PRODUCTS' },
      { labelKey: 'Data Marketplace', externalPath: '/data-marketplace' },
      { labelKey: 'Data Marketplace Test', routerLink: '/data-marketplace', featureFlag: 'EXT_DATA_MARKETPLACE' },
    ],
  },
];

/** True unless the entry carries a feature flag that is currently off. */
function isAifcNavItemEnabled(item: AifcNavItem, flags: FeatureFlagCheck): boolean {
  return !item.featureFlag || flags.isEnabled(item.featureFlag);
}

/**
 * Filters `AIFC_NAV` (and each dropdown's children) by feature flag — mirroring
 * `isNavItemEnabled` / `buildProfileMenuItems` in `portal-navigation.constants.ts`.
 * Flag-less entries always survive; flagged entries are dropped while their flag
 * is off, so an internal front-ext section can be added beside its external
 * aifc-portal counterpart and stay hidden until the flag flips.
 */
export function buildAifcNav(flags: FeatureFlagCheck): AifcNavItem[] {
  return AIFC_NAV.filter((item) => isAifcNavItemEnabled(item, flags)).map((item) =>
    item.children
      ? { ...item, children: item.children.filter((child) => isAifcNavItemEnabled(child, flags)) }
      : item
  );
}
